import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, CheckCircle2, XCircle, Calendar, ArrowLeft } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import formatApiError from "../lib/apiError";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });

const COLORS = {
  used:    "#10B981",
  notUsed: "#EF4444",
  axis:    "#A1A2A6",
  grid:    "#EEEFF5",
  text:    "#262454",
};

// Number of month cards visible in the strip at a time.
const STRIP_SIZE = 6;

// ── Date helpers ────────────────────────────────────────────────────────────
function ym(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function monthLabel(d, withYear = true) {
  const m = d.toLocaleString(undefined, { month: "short" });
  return withYear ? `${m} ${d.getFullYear()}` : m;
}
function monthLong(d) {
  return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}

// Build STRIP_SIZE month anchors ending at `anchor` (anchor is the rightmost / newest month shown).
function buildStrip(anchor) {
  const arr = [];
  for (let i = STRIP_SIZE - 1; i >= 0; i--) {
    arr.push(addMonths(anchor, -i));
  }
  return arr;
}

// ── Tooltip ─────────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-label">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="chart-tooltip-row">
          <span className="chart-tooltip-swatch" style={{ background: p.color || p.fill }} />
          <span className="chart-tooltip-name">{p.name}</span>
          <span className="chart-tooltip-val">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

// Two-line X-axis tick for vertical bar charts (name on top, optional sub below, rotated -30°)
function TwoLineXTick({ x, y, payload, data, mainColor = "#262454", subColor = "#77777A" }) {
  const row = data.find((d) => d.name === payload.value) || {};
  const sub = row.sub || "";
  const trunc = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s);
  return (
    <g transform={`translate(${x},${y})`}>
      <g transform="rotate(-30)">
        <text
          x={0} y={10} textAnchor="end"
          fill={mainColor}
          style={{ fontFamily: "Sofia Sans", fontSize: 11.5, fontWeight: 600 }}
        >
          {trunc(payload.value, 22)}
        </text>
        {sub && (
          <text
            x={0} y={22} textAnchor="end"
            fill={subColor}
            style={{ fontFamily: "Sofia Sans", fontSize: 10 }}
          >
            {trunc(sub, 22)}
          </text>
        )}
      </g>
    </g>
  );
}

// ── Faculty calendar (L3) ────────────────────────────────────────────────
// Renders the selected month as a 7-column grid (Mon–Sun). Each cell shows the
// day number and small Used / Not-Used badges based on this faculty member's
// timetable entries for that day. Hovering a populated cell reveals a
// detailed popover listing every entry's time, lesson, status and remarks.

const REASON_LABELS = {
  content_not_available: "Content not available",
  technical_issues:      "Technical issues",
};

function formatTimeRange(start, durationMin) {
  if (!start) return "Time not set";
  if (!durationMin) return start;
  // Compute end-time
  const [hh, mm] = start.split(":").map((n) => parseInt(n, 10));
  if (isNaN(hh) || isNaN(mm)) return start;
  const total = hh * 60 + mm + durationMin;
  const eh = Math.floor((total / 60) % 24);
  const em = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${start} – ${pad(eh)}:${pad(em)} · ${durationMin} min`;
}

function FacultyCalendarCard({ selected, byDay, facultyName }) {
  const year  = selected.getFullYear();
  const month = selected.getMonth();
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  const daysInMonth = last.getDate();
  // Monday-first: shift JS Sunday=0 to 6
  const leadingBlanks = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // Pad to complete the last week (multiple of 7)
  while (cells.length % 7 !== 0) cells.push(null);

  const today = new Date();
  const isTodayCell = (d) =>
    d != null
    && today.getFullYear() === year
    && today.getMonth() === month
    && today.getDate() === d;

  const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="chart-card">
      <div className="chart-card-head">
        <div>
          <p className="chart-card-title">
            {facultyName} — Day-by-Day Activity
          </p>
          <p className="chart-card-sub">
            Each cell shows that day's classes for this faculty member. Green = Used, Red = Not Used.
          </p>
        </div>
        <div className="qv-legend-group">
          <span className="qv-legend qv-legend-used">
            <span className="qv-dot" style={{ background: COLORS.used }} /> Used
          </span>
          <span className="qv-legend qv-legend-notused">
            <span className="qv-dot" style={{ background: COLORS.notUsed }} /> Not Used
          </span>
        </div>
      </div>
      <div className="qv-calendar" data-testid="qv-calendar">
        <div className="qv-cal-weekdays">
          {weekdays.map((w) => (
            <div key={w} className="qv-cal-weekday">{w}</div>
          ))}
        </div>
        <div className="qv-cal-grid">
          {cells.map((d, i) => {
            if (d == null) {
              return <div key={`b-${i}`} className="qv-cal-cell qv-cal-cell-blank" />;
            }
            const info = byDay[d];
            const used     = info?.used    || 0;
            const notUsed  = info?.notUsed || 0;
            const dayEntries = info?.entries || [];
            let stateClass = "qv-cal-cell-empty";
            if (used > 0 && notUsed > 0)      stateClass = "qv-cal-cell-mixed";
            else if (used > 0)                stateClass = "qv-cal-cell-used";
            else if (notUsed > 0)             stateClass = "qv-cal-cell-notused";
            const todayClass = isTodayCell(d) ? " qv-cal-cell-today" : "";
            const col = i % 7;
            const row = Math.floor(i / 7);
            const totalRows = cells.length / 7;
            // Edge-aware popover placement so it stays visible
            const popAlign =
              col <= 1 ? " qv-cal-popover-left"
              : col >= 5 ? " qv-cal-popover-right"
              : "";
            const popSide = row >= totalRows - 2 ? " qv-cal-popover-above" : " qv-cal-popover-below";
            const dayLabel = `${d} ${selected.toLocaleString(undefined, { month: "short", year: "numeric" })}`;
            return (
              <div
                key={`d-${d}`}
                className={`qv-cal-cell ${stateClass}${todayClass}`}
                data-testid={`qv-cal-day-${d}`}
              >
                <div className="qv-cal-day-num">{d}</div>
                {(used > 0 || notUsed > 0) && (
                  <div className="qv-cal-counts">
                    {used > 0 && (
                      <span className="qv-cal-pill qv-cal-pill-used">{used}</span>
                    )}
                    {notUsed > 0 && (
                      <span className="qv-cal-pill qv-cal-pill-notused">{notUsed}</span>
                    )}
                  </div>
                )}

                {/* Rich hover popover — only when the day has entries */}
                {dayEntries.length > 0 && (
                  <div
                    className={`qv-cal-popover${popAlign}${popSide}`}
                    role="tooltip"
                    data-testid={`qv-cal-popover-${d}`}
                  >
                    <div className="qv-cal-popover-head">
                      <span className="qv-cal-popover-date">{dayLabel}</span>
                      <span className="qv-cal-popover-totals">
                        {used > 0 && (
                          <span className="qv-cal-popover-stat qv-cal-popover-stat-used">
                            {used} used
                          </span>
                        )}
                        {notUsed > 0 && (
                          <span className="qv-cal-popover-stat qv-cal-popover-stat-notused">
                            {notUsed} not used
                          </span>
                        )}
                      </span>
                    </div>
                    <ul className="qv-cal-popover-list">
                      {dayEntries.map((en) => (
                        <li key={en.id} className="qv-cal-entry">
                          <div className="qv-cal-entry-row1">
                            <span className="qv-cal-entry-time">
                              {formatTimeRange(en.start_time, en.duration_minutes)}
                            </span>
                            <span
                              className={`qv-cal-entry-status ${en.medimagic_used ? "is-used" : "is-notused"}`}
                            >
                              {en.medimagic_used ? "Used" : "Not Used"}
                            </span>
                          </div>
                          <div className="qv-cal-entry-lesson">
                            {en.lesson_name || "Lesson"}
                            {en.lesson_code && (
                              <span className="qv-cal-entry-code"> · {en.lesson_code}</span>
                            )}
                          </div>
                          {(en.subject_name || en.subject_code) && (
                            <div className="qv-cal-entry-subject">
                              {en.subject_name}
                              {en.subject_code && (
                                <span className="qv-cal-entry-code"> · {en.subject_code}</span>
                              )}
                            </div>
                          )}
                          {!en.medimagic_used && en.cancellation_reason && (
                            <div className="qv-cal-entry-reason">
                              <span className="qv-cal-entry-reason-label">Reason:</span>{" "}
                              {REASON_LABELS[en.cancellation_reason] || en.cancellation_reason}
                            </div>
                          )}
                          {!en.medimagic_used && en.cancellation_description && (
                            <div className="qv-cal-entry-desc">
                              <span className="qv-cal-entry-desc-label">Details:</span>{" "}
                              {en.cancellation_description}
                            </div>
                          )}
                          {en.remarks && (
                            <div className="qv-cal-entry-remarks">
                              <span className="qv-cal-entry-desc-label">Remarks:</span>{" "}
                              {en.remarks}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const today = useMemo(() => startOfMonth(new Date()), []);
  // The rightmost month in the strip. Defaults to current month.
  const [anchor, setAnchor] = useState(today);
  // The currently selected month (default = current month).
  const [selected, setSelected] = useState(today);
  // Drill-down state — four levels:
  //   level 0: top — by college
  //   level 1: drillCollege set — by subject (within that college)
  //   level 2: drillCollege + drillSubject set — by professor (within that subject + college)
  //   level 3: + drillFaculty set — calendar view for that professor (within college + subject)
  const [drillCollege, setDrillCollege]     = useState(null);
  const [drillSubject, setDrillSubject]     = useState(null);
  const [drillFaculty, setDrillFaculty] = useState(null);
  const level = drillFaculty ? 3 : drillSubject ? 2 : drillCollege ? 1 : 0;

  const [colleges, setColleges] = useState([]);
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [co, tt] = await Promise.all([
        axios.get(`${API}/api/colleges`, ax()),
        axios.get(`${API}/api/timetable`, ax()),
      ]);
      setColleges(co.data);
      setEntries(tt.data);
    } catch (e) {
      setError(formatApiError(e, "Failed to load Quick View data."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Month strip (cheap to compute — no memo needed)
  const strip = buildStrip(anchor);
  const canGoForward = anchor.getTime() < today.getTime();

  const selectedYM = ym(selected);

  // Aggregate timetable entries for the selected month —
  //   L0: by college, L1: by subject, L2: by professor.
  //   L3 uses calendarEntries instead of chartData.
  const chartData = useMemo(() => {
    if (level === 3) return [];
    const buckets = {};

    if (level === 0) {
      // Seed with ALL colleges so empty colleges still appear.
      for (const c of colleges) {
        const parts = [c.city_name, c.state_name].filter(Boolean);
        buckets[c.id] = {
          id: c.id,
          name: c.name,
          sub: parts.join(" · "),
          used: 0,
          notUsed: 0,
        };
      }
      for (const e of entries) {
        if (!e.date || e.date.slice(0, 7) !== selectedYM) continue;
        const b = buckets[e.college_id];
        if (!b) continue;
        if (e.medimagic_used) b.used += 1;
        else b.notUsed += 1;
      }
    } else if (level === 1) {
      // Subjects in the selected college (selected month).
      for (const e of entries) {
        if (e.college_id !== drillCollege.id) continue;
        if (!e.date || e.date.slice(0, 7) !== selectedYM) continue;
        const sid = e.subject_id || "_unknown";
        if (!buckets[sid]) {
          buckets[sid] = {
            id: sid,
            name: e.subject_name || "Unknown subject",
            sub: e.subject_code || "",
            used: 0,
            notUsed: 0,
          };
        }
        if (e.medimagic_used) buckets[sid].used += 1;
        else buckets[sid].notUsed += 1;
      }
    } else {
      // Faculty teaching the selected subject in the selected college (selected month).
      for (const e of entries) {
        if (e.college_id !== drillCollege.id) continue;
        if (e.subject_id !== drillSubject.id) continue;
        if (!e.date || e.date.slice(0, 7) !== selectedYM) continue;
        const pid = e.professor_id || "_unknown";
        if (!buckets[pid]) {
          buckets[pid] = {
            id: pid,
            name: e.professor_name || "Unknown faculty",
            sub: "",
            used: 0,
            notUsed: 0,
          };
        }
        if (e.medimagic_used) buckets[pid].used += 1;
        else buckets[pid].notUsed += 1;
      }
    }
    return Object.values(buckets);
  }, [entries, colleges, selectedYM, drillCollege, drillSubject, level]);

  // L3: entries for the selected professor + subject + college, indexed by day (1-31).
  const calendarByDay = useMemo(() => {
    if (level !== 3) return {};
    const out = {};
    for (const e of entries) {
      if (e.college_id !== drillCollege.id) continue;
      if (e.subject_id !== drillSubject.id) continue;
      if (e.professor_id !== drillFaculty.id) continue;
      if (!e.date || e.date.slice(0, 7) !== selectedYM) continue;
      // e.date is "YYYY-MM-DD"
      const day = parseInt(e.date.slice(8, 10), 10);
      if (!out[day]) out[day] = { used: 0, notUsed: 0, entries: [] };
      if (e.medimagic_used) out[day].used += 1;
      else out[day].notUsed += 1;
      out[day].entries.push(e);
    }
    return out;
  }, [entries, selectedYM, drillCollege, drillSubject, drillFaculty, level]);

  const usedSorted = useMemo(
    () => [...chartData].sort((a, b) => a.name.localeCompare(b.name)),
    [chartData]
  );

  const totals = useMemo(() => {
    if (level === 3) {
      const used = Object.values(calendarByDay).reduce((s, d) => s + d.used, 0);
      const notUsed = Object.values(calendarByDay).reduce((s, d) => s + d.notUsed, 0);
      return { used, notUsed, total: used + notUsed };
    }
    const used = chartData.reduce((s, r) => s + r.used, 0);
    const notUsed = chartData.reduce((s, r) => s + r.notUsed, 0);
    return { used, notUsed, total: used + notUsed };
  }, [chartData, calendarByDay, level]);

  // Vertical bar charts — fixed body height. Width auto-fits the container.
  const chartHeight = 380;

  // Drill-down click: L0→subject, L1→professor, L2→calendar (L3), L3→no-op.
  const onBarClick = (data) => {
    if (!data) return;
    if (level === 0) {
      setDrillCollege({ id: data.id, name: data.name, sub: data.sub });
      setDrillSubject(null);
      setDrillFaculty(null);
    } else if (level === 1) {
      setDrillSubject({ id: data.id, name: data.name, sub: data.sub });
      setDrillFaculty(null);
    } else if (level === 2) {
      setDrillFaculty({ id: data.id, name: data.name });
    }
  };

  const goBack = () => {
    if (level === 3) setDrillFaculty(null);
    else if (level === 2) setDrillSubject(null);
    else if (level === 1) setDrillCollege(null);
  };

  // Dynamic labels based on level
  const dimensionLabel =
    level === 0 ? "College" : level === 1 ? "Subject" : level === 2 ? "Faculty" : "Day";
  const dimensionLabelPlural =
    level === 0 ? "colleges" : level === 1 ? "subjects" : level === 2 ? "faculty" : "days";
  const backLabel =
    level === 3 ? "Back to Faculty"
    : level === 2 ? "Back to Subjects"
    : level === 1 ? "Back to Colleges"
    : "";
  const canClickBars = level < 3;

  if (loading) {
    return (
      <div className="page-wrap">
        <div className="page-header">
          <h1 className="page-title">Quick View</h1>
          <p className="page-sub">Loading…</p>
        </div>
        <div className="table-loading">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrap">
        <div className="table-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Quick View</h1>
          <p className="page-sub">
            {level === 0 && (
              <>Monthly Medimagic usage by college — selected: <strong>{monthLong(selected)}</strong></>
            )}
            {level === 1 && (
              <>
                Subjects in <strong>{drillCollege.name}</strong>
                {drillCollege.sub && <span className="qv-bc-sub"> · {drillCollege.sub}</span>}
                {" — "}selected: <strong>{monthLong(selected)}</strong>
              </>
            )}
            {level === 2 && (
              <>
                Faculty teaching <strong>{drillSubject.name}</strong>
                {" in "}<strong>{drillCollege.name}</strong>
                {" — "}selected: <strong>{monthLong(selected)}</strong>
              </>
            )}
            {level === 3 && (
              <>
                <strong>{drillFaculty.name}</strong>
                {" — "}calendar for <strong>{monthLong(selected)}</strong>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Back + breadcrumb row (only when drilled) */}
      {level > 0 && (
        <div className="qv-nav-row" data-testid="qv-nav-row">
          <button
            className="qv-back-btn"
            onClick={goBack}
            data-testid="qv-back-btn"
            aria-label={backLabel}
          >
            <span className="qv-back-btn-icon" aria-hidden="true">
              <ArrowLeft size={16} />
            </span>
            <span className="qv-back-btn-label">{backLabel}</span>
          </button>

          <div className="qv-breadcrumb" data-testid="qv-breadcrumb">
            <button
              className="qv-bc-link"
              onClick={() => { setDrillCollege(null); setDrillSubject(null); setDrillFaculty(null); }}
              data-testid="qv-bc-colleges"
            >
              Colleges
            </button>
            <span className="qv-bc-sep">/</span>

            {level === 1 && (
              <span className="qv-bc-current">{drillCollege.name}</span>
            )}

            {level === 2 && (
              <>
                <button
                  className="qv-bc-link"
                  onClick={() => { setDrillSubject(null); setDrillFaculty(null); }}
                  data-testid="qv-bc-subjects"
                >
                  {drillCollege.name}
                </button>
                <span className="qv-bc-sep">/</span>
                <span className="qv-bc-current">{drillSubject.name}</span>
              </>
            )}

            {level === 3 && (
              <>
                <button
                  className="qv-bc-link"
                  onClick={() => { setDrillSubject(null); setDrillFaculty(null); }}
                  data-testid="qv-bc-subjects"
                >
                  {drillCollege.name}
                </button>
                <span className="qv-bc-sep">/</span>
                <button
                  className="qv-bc-link"
                  onClick={() => setDrillFaculty(null)}
                  data-testid="qv-bc-faculty"
                >
                  {drillSubject.name}
                </button>
                <span className="qv-bc-sep">/</span>
                <span className="qv-bc-current">{drillFaculty.name}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Month strip */}
      <div className="qv-month-strip" data-testid="qv-month-strip">
        <button
          className="qv-month-arrow"
          onClick={() => setAnchor((a) => addMonths(a, -1))}
          aria-label="Previous month"
          data-testid="qv-month-prev"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="qv-month-cards">
          {strip.map((d) => {
            const isSelected = ym(d) === ym(selected);
            const isCurrent  = ym(d) === ym(today);
            const isLast     = ym(d) === ym(addMonths(today, -1));
            let tag = "";
            if (isCurrent) tag = "Current";
            else if (isLast) tag = "Last month";
            return (
              <button
                key={ym(d)}
                className={`qv-month-card${isSelected ? " selected" : ""}${isCurrent ? " current" : ""}`}
                onClick={() => setSelected(d)}
                data-testid={`qv-month-${ym(d)}`}
              >
                {tag && <span className="qv-month-tag">{tag}</span>}
                <span className="qv-month-name">{monthLabel(d, false)}</span>
                <span className="qv-month-year">{d.getFullYear()}</span>
              </button>
            );
          })}
        </div>

        <button
          className="qv-month-arrow"
          onClick={() => setAnchor((a) => addMonths(a, 1))}
          disabled={!canGoForward}
          aria-label="Next month"
          data-testid="qv-month-next"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* KPI row */}
      <div className="kpi-strip">
        <div className="kpi-item">
          <Calendar size={16} />
          <span className="kpi-label">Selected month</span>
          <span className="kpi-value">{monthLong(selected)}</span>
        </div>
        <div className="kpi-item kpi-item-used">
          <CheckCircle2 size={16} />
          <span className="kpi-label">Used</span>
          <span className="kpi-value">{totals.used}/{totals.total}</span>
        </div>
        <div className="kpi-item kpi-item-not-used">
          <XCircle size={16} />
          <span className="kpi-label">Not Used</span>
          <span className="kpi-value">{totals.notUsed}/{totals.total}</span>
        </div>
        <div className="kpi-item">
          <span className="kpi-label">Usage percentage</span>
          <span className="kpi-value">
            {totals.total ? `${((totals.used / totals.total) * 100).toFixed(1)}%` : "—"}
          </span>
        </div>
      </div>

      {/* L0–L2: vertical bar charts. L3: calendar (rendered below). */}
      {level < 3 && (<>
      {/* Used — by current dimension (vertical bars) */}
      <div className="chart-card">
        <div className="chart-card-head">
          <div>
            <p className="chart-card-title">Medimagic Used — by {dimensionLabel}</p>
            <p className="chart-card-sub">
              {level === 0 && `Classes that used Medimagic in ${monthLong(selected)}`}
              {level === 1 && `Classes that used Medimagic in ${drillCollege.name} during ${monthLong(selected)}`}
              {level === 2 && `Classes that used Medimagic for ${drillSubject.name} in ${drillCollege.name} during ${monthLong(selected)}`}
              {canClickBars && (
                <span className="qv-hint">
                  {" "}· click a bar to drill into {level === 0 ? "subjects" : "faculty"}
                </span>
              )}
            </p>
          </div>
          <span className="qv-legend qv-legend-used">
            <span className="qv-dot" style={{ background: COLORS.used }} /> Used
          </span>
        </div>
        <div className="chart-body" style={{ height: chartHeight }}>
          {chartData.length === 0 ? (
            <div className="empty-state">
              <p>No {dimensionLabelPlural} with entries this month.</p>
            </div>
          ) : (
            <ResponsiveContainer>
              <BarChart
                data={usedSorted}
                margin={{ top: 8, right: 16, left: 12, bottom: 70 }}
              >
                <CartesianGrid stroke={COLORS.grid} vertical={false} />
                <XAxis
                  type="category"
                  dataKey="name"
                  interval={0}
                  axisLine={{ stroke: COLORS.grid }}
                  tickLine={false}
                  height={70}
                  // eslint-disable-next-line react/no-unstable-nested-components
                  tick={(p) => <TwoLineXTick {...p} data={usedSorted} />}
                />
                <YAxis
                  type="number"
                  tick={{ fill: COLORS.axis, fontSize: 11, fontFamily: "Sofia Sans" }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(16, 185, 129, 0.08)" }} />
                <Legend
                  wrapperStyle={{ paddingTop: 6, fontFamily: "Sofia Sans", fontSize: 12 }}
                  iconType="circle"
                  iconSize={8}
                />
                <Bar
                  dataKey="used"
                  name="Used"
                  fill={COLORS.used}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={56}
                  onClick={canClickBars ? onBarClick : undefined}
                  style={{ cursor: canClickBars ? "pointer" : "default" }}
                >
                  {usedSorted.map((row) => (
                    <Cell key={row.id} fill={row.used === 0 ? "#D6F5E6" : COLORS.used} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Not Used chart removed per request — single "Used" chart only */}
      </>)}

      {/* L3: Professor calendar */}
      {level === 3 && (
        <FacultyCalendarCard
          selected={selected}
          byDay={calendarByDay}
          facultyName={drillFaculty.name}
        />
      )}
    </div>
  );
}
